/**
 * MasonrySearch Class
 * Handles the combination of Masonry grid layout and search/filtering functionality.
 *
 * Example:
 * const masonrySearch = new MasonrySearch({
 *   searchInputSelector: ".header-with-search__input",
 *   defaultIconSelector: ".header-with-search__default-icon",
 *   activeIconSelector: ".header-with-search__active-icon",
 *   resultsTextSelector: ".logo-list__search-results-text",
 *   headerSelector: ".header-with-search__header",
 *   containerSelector: ".logo-list__list",
 *   itemSelector: ".logo-list__item"
 * });
 *
 * Ensure the HTML structure matches the selectors provided in the configuration.
 */

// eslint-disable-next-line no-unused-vars
class MasonrySearch {
  /**
   * Creates a new instance of MasonrySearch.
   * @param {Object} config - Configuration object for MasonrySearch.
   * @param {string} config.searchInputSelector - Selector for the search input field.
   * @param {string} config.defaultIconSelector - Selector for the default (inactive) search icon.
   * @param {string} config.activeIconSelector - Selector for the active search icon.
   * @param {string} config.resultsTextSelector - Selector for the search results text element.
   * @param {string} config.headerSelector - Selector for the header element (used for scroll positioning).
   * @param {string} config.containerSelector - Selector for the container element holding grid items.
   * @param {string} config.itemSelector - Selector for individual grid items.
   */
  constructor(config) {
    this.config = config;

    this.searchInput = document.querySelector(config.searchInputSelector);
    this.defaultIcon = document.querySelector(config.defaultIconSelector);
    this.activeIcon = document.querySelector(config.activeIconSelector);
    this.searchResultsText = document.querySelector(config.resultsTextSelector);
    this.header = document.querySelector(config.headerSelector);
    this.container = document.querySelector(config.containerSelector);
    this.allItems = Array.from(document.querySelectorAll(config.itemSelector));

    this.removedItems = new Set();
    this.originalSearchInputPosition =
      this.searchInput?.getBoundingClientRect().top + window.scrollY || 0;

    this.msnry = null;

    // Selectors are document-wide, so every instance on the page resolves to the
    // same container. Only the first one may drive it.
    if (this.isValidConfig() && !this.container.dataset.masonrySearch) {
      this.container.dataset.masonrySearch = "true";
      this.loadMasonry();
    }
  }

  /**
   * Validates if all required elements exist.
   * @returns {boolean} True if all required elements are found, otherwise false.
   */
  isValidConfig() {
    return this.searchInput && this.container && this.allItems.length > 0;
  }

  /**
   * Dynamically loads the Masonry library and initializes the grid.
   */
  loadMasonry() {
    const masonryScript = document.createElement("script");
    masonryScript.src =
      "https://cdnjs.cloudflare.com/ajax/libs/masonry/4.2.2/masonry.pkgd.min.js";
    document.head.appendChild(masonryScript);

    masonryScript.onload = () => {
      this.init();
    };
  }

  /**
   * Initializes the grid, sets up event listeners, and appends items to the container.
   */
  init() {
    // A tile from another section's grid keeps waiting on that section's
    // <logo-list>, which this move leaves empty and collapsed at the foot of
    // the page — so its reveal would not fire until the very bottom.
    const adopted = this.allItems.filter(
      (item) =>
        item.parentElement !== this.container && item.hasAttribute("reveal")
    );

    adopted.forEach((item) =>
      item.closest("logo-list")?.intersectionObserver?.disconnect()
    );

    const native = this.allItems.filter(
      (item) => item.parentElement === this.container
    );

    this.allItems.forEach((item) => this.container.appendChild(item));

    this.updateGutter();
    window.addEventListener("resize", () => this.updateGutter());

    this.searchInput.addEventListener("input", () => this.handleSearchInput());
    this.activeIcon.addEventListener("click", () => this.clearSearch());

    this.revealAdopted(adopted, native);
    this.removePlaceholders();
  }

  /**
   * Continues the destination grid's reveal across the adopted tiles, so they
   * animate in one after another behind its own last tile — the sequence the
   * theme would have run had every tile started in this grid.
   * @param {Element[]} items - Tiles moved in from another section's grid.
   * @param {Element[]} native - Tiles this grid already held.
   */
  revealAdopted(items, native) {
    if (items.length === 0) {
      return;
    }

    const host = this.container.closest("logo-list");
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const play = () =>
      items.forEach((item, index) => {
        const animation = item.animate(
          {
            opacity: [0, 1],
            transform: [`translateY(${still ? 0 : "30px"})`, "translateY(0)"],
          },
          { duration: 300, delay: still ? 0 : 100 * index, easing: "ease" }
        );

        // The theme pins opacity on finish rather than filling forwards;
        // without it the tile snaps back to the [reveal] rule's opacity: 0.
        animation.addEventListener("finish", () => {
          item.style.opacity = "1";
        });
      });

    // Wait out the reveal already running on this grid's own tiles, so the
    // first adopted tile follows its last one on the same cadence.
    const follow = () =>
      Promise.allSettled(
        native.flatMap((item) => item.getAnimations().map((a) => a.finished))
      ).then(play);

    if (!host || !host.hasAttribute("reveal-on-scroll")) {
      play();
    } else if (host.classList.contains("became-visible")) {
      follow();
    } else {
      // The grid has not scrolled into view yet, so its reveal has not started.
      const observer = new MutationObserver(() => {
        if (!host.classList.contains("became-visible")) {
          return;
        }
        observer.disconnect();
        requestAnimationFrame(follow);
      });

      observer.observe(host, { attributes: true, attributeFilter: ["class"] });
    }
  }

  /**
   * Initializes or updates the Masonry grid layout with a specified gutter size.
   * @param {number} gutter - The gutter spacing between grid items.
   */
  initializeMasonry(gutter) {
    if (this.msnry) {
      this.msnry.destroy();
    }
    // eslint-disable-next-line no-undef
    this.msnry = new Masonry(this.container, {
      itemSelector: this.config.itemSelector,
      columnWidth: this.config.itemSelector,
      gutter: gutter,
      resize: true,
      fitWidth: true,
      transitionDuration: 0,
    });
  }

  /**
   * Updates the gutter size based on the screen width (responsive behavior).
   */
  updateGutter() {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const gutter = isMobile ? 14 : 20;
    this.initializeMasonry(gutter);
  }

  /**
   * Handles search input events and filters grid items based on the search query.
   */
  handleSearchInput() {
    const query = this.searchInput.value.toLowerCase();
    const currentScrollPosition = window.scrollY;

    if (currentScrollPosition > this.originalSearchInputPosition) {
      window.scrollTo({
        top: this.header?.offsetHeight || 0,
        behavior: "smooth",
      });
      setTimeout(() => this.filterItems(query), 100);
    } else {
      this.filterItems(query);
    }

    this.toggleIcons(query.length > 0);
  }

  /**
   * Filters grid items based on the search query.
   * @param {string} query - The search query entered by the user.
   */
  filterItems(query) {
    let resultCount = 0;

    this.allItems.forEach((item) => {
      const textContent = item.textContent.toLowerCase();
      const matchesQuery = textContent.includes(query);

      if (matchesQuery) {
        if (!this.container.contains(item)) {
          this.container.appendChild(item);
          this.removedItems.delete(item);
        }
        resultCount++;
      } else {
        if (this.container.contains(item)) {
          this.container.removeChild(item);
          this.removedItems.add(item);
        }
      }
    });

    this.msnry.layout();

    if (query) {
      const resultText = resultCount === 0 ? "No" : resultCount;
      const resultWord = resultCount === 1 ? "result" : "results";
      this.searchResultsText.textContent = `${resultText} ${resultWord} found for "${this.searchInput.value}"`;
      this.searchResultsText.classList.remove("hidden-animated");
    } else {
      this.searchResultsText.textContent = "";
      this.searchResultsText.classList.add("hidden-animated");
    }
  }

  /**
   * Clears the search input and resets the grid to its original state.
   */
  clearSearch() {
    this.searchInput.value = "";
    this.toggleIcons(false);
    this.searchResultsText.textContent = "";
    this.searchResultsText.classList.add("hidden-animated");

    this.removedItems.forEach((item) => {
      this.container.appendChild(item);
    });
    this.removedItems.clear();

    this.msnry.layout();
  }

  /**
   * Toggles the visibility of the default and active search icons.
   * @param {boolean} isSearching - True if search input is active, false otherwise.
   */
  toggleIcons(isSearching) {
    this.defaultIcon.classList.toggle("hidden", isSearching);
    this.activeIcon.classList.toggle("hidden", !isSearching);
  }

  /**
   * Removes the placeholder items from the DOM after Masonry initializes.
   */
  removePlaceholders() {
    document
      .querySelectorAll(".logo-list__placeholder-list")
      .forEach((list) => list.remove());
  }
}
